import React, { useState } from "react";
import Box from "@mui/material/Box";
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import GeniePage from "../../../components/special/genie-page/GeniePage";
import GenieText from "../../../components/core/genie-text/GenieText";
import useBuyerOrders from "../../../queries/routes/useBuyerOrders";
import { useParams } from "react-router-dom";
import { formatCurrency } from "../../../utils/currency";
import { readableDateTime } from "../../../utils/date";
import GenieTableLoad from "../../../components/core/genie-table-load/GenieTableLoad";
import GenieEmpty from "../../../components/core/genie-empty/GenieEmpty";
import useUser from "../../../queries/routes/useUser";
import ViewOrderDialog from "../../pages/customers/ViewOrderDialog";
import { Order } from "../../../types/api/Order";
import { theme } from "../../../components/special/genie-theme/genie-theme";

function CustomerOrders() {
  const { org_id } = useParams();
  const { data: user } = useUser();
  const { data: orders, isLoading } = useBuyerOrders(
    user?.organizations[0].id!
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  function setClosed() {
    setDialogOpen(false);
  }

  return (
    <GeniePage>
      <ViewOrderDialog
        order={selectedOrder}
        open={dialogOpen}
        setClosed={setClosed}
      />
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <GenieText fontSize={"medium"} fontWeight={"bold"}>
          Orders
        </GenieText>
        <GenieText
          link={`/storefront/${org_id}`}
          style={{ cursor: "pointer" }}
          fontSize={"tiny"}
          underline
        >
          Back to store
        </GenieText>
      </Box>
      <Box mt={4}>
        {isLoading && (
          <GenieTableLoad
            rowCount={10}
            columnCount={4}
            sx={{ minWidth: 650 }}
          />
        )}
        {!isLoading && orders?.length === 0 && (
          <Box mb={2}>
            <GenieEmpty>
              You don't have any orders yet. Place an order below.
            </GenieEmpty>
          </Box>
        )}
        {!isLoading && orders && orders.length > 0 && (
          <TableContainer component={Box}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Shipped?</TableCell>
                  <TableCell align="right">Invoice</TableCell>
                  <TableCell align="right">Payment Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders?.map((order) => (
                  <TableRow
                    key={order.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row" align={"left"}>
                      {readableDateTime(order.completed_on)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(
                        order.cart.total_cents +
                          (order.shipping_cost_in_cents
                            ? order.shipping_cost_in_cents
                            : 0)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {order.shipping_information ? "Yes" : "No"}
                    </TableCell>
                    <TableCell align="right">
                      {order.invoice?.link ? (
                        <GenieText
                          blank={true}
                          fontSize={"tiny"}
                          link={order.invoice?.link || "#"}
                          uppercase
                        >
                          Payment Invoice
                        </GenieText>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={
                          order.invoice?.stripe_status?.toUpperCase() || "N/A"
                        }
                        variant={"outlined"}
                        color={"primary"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <GenieText
                        style={{
                          cursor: "pointer",
                          color: theme.palette.primary.main,
                        }}
                        underline={true}
                        fontSize={"tiny"}
                        onClick={() => {
                          setDialogOpen(true);
                          setSelectedOrder(order);
                        }}
                      >
                        View Order
                      </GenieText>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </GeniePage>
  );
}

export default CustomerOrders;
